import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { App as AntdApp } from 'antd';
import Api from '@/services/api';
import { PortalUser, UserPermission } from '@/types';

export type PlanFeatures = Record<string, boolean>;
export type PlanLimits = Record<string, number | null>;

// Debug logging helper - persiste no localStorage para capturar erros antes de redirecionamento
const debugLog = (step: string, data?: any) => {
  const timestamp = new Date().toISOString();
  const logEntry = { timestamp, step, data };
  // Apenas armazenar em localStorage sem log no console durante execução normal
  try {
    const logs = JSON.parse(localStorage.getItem('auth_debug_logs') || '[]');
    logs.push(logEntry);
    // Manter apenas últimos 50 logs
    if (logs.length > 50) logs.shift();
    localStorage.setItem('auth_debug_logs', JSON.stringify(logs));
  } catch (e) {
    console.error('Failed to save debug log', e);
  }
};

// Função para visualizar logs no console
(window as any).showAuthLogs = () => {
  const logs = JSON.parse(localStorage.getItem('auth_debug_logs') || '[]');
  // eslint-disable-next-line no-console
  console.table(logs);
  return logs;
};

(window as any).clearAuthLogs = () => {
  localStorage.removeItem('auth_debug_logs');
  // eslint-disable-next-line no-console
  console.log('Auth logs cleared');
};

type User = {
  id: number;
  name: string;
  email?: string;
  role_title?: string;
  department?: string;
  pronouns?: string;
  timezone?: string;
  tenant_id?: number | null;
  is_superadmin?: boolean;
  is_admin: boolean;
  is_approved: boolean;
  permission: UserPermission;
  profile_photo_url?: string | null;
  profile_photo_updated_at?: string | null; // T061: Timestamp for cache-busting
  audit_enabled?: boolean; // T022: Audit feature flag state
  plan?: string;
  plan_features?: PlanFeatures;
  plan_limits?: PlanLimits;
} | null;

type AuthContextType = {
  user: User;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  hasPermission: (permission: UserPermission) => boolean;
  hasFeature: (featureName: string) => boolean;
  plan?: string;
  planFeatures?: PlanFeatures;
  planLimits?: PlanLimits;
  auditEnabled: boolean; // T022: Audit feature flag state from session
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export AuthContext for testing
export { AuthContext };

const PERMISSION_ORDER: UserPermission[] = ['read', 'edit_read', 'edit_read_delete', 'admin'];

const normalizeUser = (raw: PortalUser | null | undefined): User => {
  if (!raw) return null;
  
  // Debug: Log raw user data from backend
  debugLog('NORMALIZE_USER_RAW', {
    id: raw.id,
    name: raw.name,
    is_superadmin: (raw as any).is_superadmin,
    is_admin: raw.is_admin,
    permission: raw.permission,
    raw_keys: Object.keys(raw)
  });
  
  // Super admin should always have 'admin' permission (highest level)
  let permission: UserPermission = 'read';
  if ((raw as any).is_superadmin) {
    permission = 'admin';
    debugLog('NORMALIZE_USER_SUPERADMIN_DETECTED', { permission: 'admin' });
  } else if (PERMISSION_ORDER.includes(raw.permission)) {
    permission = raw.permission as UserPermission;
    debugLog('NORMALIZE_USER_REGULAR_PERMISSION', { permission });
  } else {
    debugLog('NORMALIZE_USER_DEFAULT_PERMISSION', { permission: 'read', original: raw.permission });
  }
  
  return {
    id: raw.id,
    name: raw.name,
    email: raw.email ?? undefined,
    role_title: raw.role_title ?? undefined,
    department: raw.department ?? undefined,
    pronouns: raw.pronouns ?? undefined,
    timezone: raw.timezone ?? undefined,
    tenant_id: (raw as any).tenant_id ?? null,
    is_superadmin: Boolean((raw as any).is_superadmin),
    is_admin: !!raw.is_admin,
    is_approved: !!raw.is_approved,
    permission,
    profile_photo_url: raw.profile_photo_url ?? undefined,
    profile_photo_updated_at: (raw as any).profile_photo_updated_at ?? undefined, // T061: Cache-busting timestamp
    audit_enabled: (raw as any).audit_enabled ?? true, // T023: Default to true if not present
    plan: (raw as any).plan ?? 'free',
    plan_features: (raw as any).plan_features ?? {},
    plan_limits: (raw as any).plan_limits ?? {},
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const api = new Api();
  const [user, setUser] = useState<User>(null);
  const [loading, setLoading] = useState(true);
  const { message } = AntdApp.useApp();

  const refresh = async () => {
    try {
      debugLog('REFRESH_START');
      
      const me = await api.me();
      debugLog('REFRESH_API_ME_RESPONSE', me);
      
      const normalized = normalizeUser(me);
      debugLog('REFRESH_USER_NORMALIZED', normalized);
      
      setUser(normalized);
      
      // Persist is_superadmin flag to sessionStorage for API interceptor (T036)
      // sessionStorage is preferred over localStorage to limit XSS exposure.
      if (normalized) {
        const isSuperAdminValue = !!normalized.is_superadmin;
        sessionStorage.setItem('is_superadmin', String(isSuperAdminValue));
        debugLog('REFRESH_SUCCESS', { isSuperAdminValue });
        
        // CRITICAL FIX: Clear selectedTenantId for non-superadmin users
        // This prevents "attempted unauthorized access" errors when localStorage
        // has residual selectedTenantId from a previous superadmin session
        if (!isSuperAdminValue) {
          localStorage.removeItem('selectedTenantId');
          debugLog('REFRESH_CLEARED_TENANT_ID_FOR_NON_SUPERADMIN');
        }
      } else {
        sessionStorage.removeItem('is_superadmin');
        localStorage.removeItem('selectedTenantId');
        debugLog('REFRESH_NO_USER');
      }
    } catch (e: any) {
      debugLog('REFRESH_ERROR', {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message
      });
      
      setUser(null);
      sessionStorage.removeItem('is_superadmin');
      localStorage.removeItem('selectedTenantId'); // Also clear tenant selection on auth error
    }
  };

  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      debugLog('LOGIN_START', { email });
      
      const result = await api.login(email, password);
      debugLog('LOGIN_API_RESPONSE', { hasUser: !!result?.user, user: result?.user });
      
      const normalized = normalizeUser(result?.user);
      debugLog('LOGIN_USER_NORMALIZED', normalized);
      
      // Persist is_superadmin flag to sessionStorage for API interceptor (T036)
      if (normalized) {
        const isSuperAdminValue = !!normalized.is_superadmin;
        sessionStorage.setItem('is_superadmin', String(isSuperAdminValue));
        debugLog('LOGIN_SUPERADMIN_FLAG_SAVED', { isSuperAdminValue });
        
        // CRITICAL FIX: Clear selectedTenantId for non-superadmin users
        // This prevents "attempted unauthorized access" errors when localStorage
        // has residual selectedTenantId from a previous superadmin session
        if (!isSuperAdminValue) {
          localStorage.removeItem('selectedTenantId');
          debugLog('LOGIN_CLEARED_TENANT_ID_FOR_NON_SUPERADMIN');
        }
      }
      
      // CRITICAL FIX: Set loading to false BEFORE setting user
      // This ensures ProtectedRoute won't show spinner after successful login
      setLoading(false);
      
      // Update user state
      setUser(normalized);
      
      // Wait for React to process state updates
      await new Promise(resolve => setTimeout(resolve, 100));
      debugLog('LOGIN_SUCCESS');
      
      message.success('Login realizado com sucesso.');
    } catch (e: any) {
      debugLog('LOGIN_ERROR', {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message
      });
      
      const status = e?.response?.status;
      if (status === 403) {
        message.warning('Seu usuário está pendente de aprovação.');
      } else if (status === 401) {
        message.error('E-mail ou senha incorretos.');
      } else {
        message.error('Falha no login. Tente novamente.');
      }
      throw e;
    }
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
    // Clear is_superadmin flag from sessionStorage
    sessionStorage.removeItem('is_superadmin');
    message.success('Logout realizado.');
  };

  const hasPermission = useMemo(() => {
    const rank = new Map<UserPermission, number>(PERMISSION_ORDER.map((perm, index) => [perm, index]));
    return (permission: UserPermission) => {
      if (!user) return false;
      const userIndex = rank.get(user.permission) ?? 0;
      const requiredIndex = rank.get(permission) ?? 0;
      return userIndex >= requiredIndex;
    };
  }, [user]);

  // T023: Derive auditEnabled from user state (cached from /auth/me response)
  const auditEnabled = user?.audit_enabled ?? true;

  const hasFeature = useMemo(() => {
    return (featureName: string): boolean => {
      if (!user) return false;
      if (user.is_superadmin) return true;
      return user.plan_features?.[featureName] ?? false;
    };
  }, [user]);

  const currentPlan = user?.plan;
  const currentPlanFeatures = user?.plan_features;
  const currentPlanLimits = user?.plan_limits;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, hasPermission, hasFeature, plan: currentPlan, planFeatures: currentPlanFeatures, planLimits: currentPlanLimits, auditEnabled }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
